/*
 * TTJ Video Consultation landing page.
 * Markup: sections/ttj-video-consultation.liquid. Styles: assets/ttj-video-consultation.css.
 * Vanilla JS, no dependencies. Independent of the popup (assets/ttj-video-booking.js), which is not modified.
 *
 * Reuses the popup's storefront API contract and the existing Theme setting "API Base URL" (settings.ttj_vb_api_base,
 * passed in through data-api-base). Blank or "proxy" -> Shopify app proxy, exactly like the popup.
 *   GET  {base}/api/v1/bookings/storefront/available-slots?shopDomain=...&date=YYYY-MM-DD
 *        -> { success: true, data: { times: ["09:00", ...] } }
 *   POST {base}/api/v1/bookings/storefront
 *        body { shopDomain, customerName, customerEmail, customerPhone, date, time, categories?, notes? (only the "Specify other" text) }
 *        -> { success: true, data: { bookingDate, bookingTime } }
 * Time slots are never hardcoded and there is no mock/fallback availability.
 */
(function () {
  'use strict';

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var SLOTS_TIMEOUT_MS = 20000;
  var BOOK_TIMEOUT_MS = 30000; // free-tier hosts can take a while to wake up
  var MAX_DAYS_AHEAD = 120;
  var OTHER_MAX = 200;
  var NAME_MAX = 50;
  var EMAIL_MAX = 160;

  /* ---------- helpers ---------- */

  function pad(n) { return (n < 10 ? '0' : '') + n; }

  function isoFromDate(d) {
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
  }

  function todayIso() { return isoFromDate(new Date()); }

  function maxIso() {
    var d = new Date();
    d.setDate(d.getDate() + MAX_DAYS_AHEAD);
    return isoFromDate(d);
  }

  function isIsoDate(v) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return false;
    var p = v.split('-');
    var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
    return d.getFullYear() === Number(p[0]) && d.getMonth() === Number(p[1]) - 1 && d.getDate() === Number(p[2]);
  }

  function isSelectableDate(v) {
    return isIsoDate(v) && v >= todayIso() && v <= maxIso();
  }

  // "2026-10-06" (or an ISO datetime starting with it) -> "06 October 2026"
  function formatDate(v) {
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
    if (!m) return String(v || '');
    return m[3] + ' ' + MONTHS[Number(m[2]) - 1] + ' ' + m[1];
  }

  // "14:30" -> "2:30 PM"; anything else is shown as received
  function formatTime(v) {
    var m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(v || '').trim());
    if (!m) return String(v || '');
    var h = Number(m[1]);
    var suffix = h >= 12 ? 'PM' : 'AM';
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + ':' + m[2] + ' ' + suffix;
  }

  function isValidTimeValue(v) {
    return typeof v === 'string' && /^([01]\d|2[0-3]):[0-5]\d$/.test(v);
  }

  function show(el, on) {
    if (!el) return;
    if (on) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
  }

  function setText(el, text) { if (el) el.textContent = text; }

  // Console-only diagnostics for support. Does not change any UI, request or error message.
  function diag(msg, info) {
    if (window.console && typeof console.warn === 'function') console.warn('[TTJ video consultation page] ' + msg, info);
  }

  function request(url, options, timeoutMs) {
    options = options || {};
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = null;
    var timedOut = false;
    if (ctrl) {
      options.signal = ctrl.signal;
      timer = setTimeout(function () { timedOut = true; ctrl.abort(); }, timeoutMs);
    }
    return fetch(url, options).then(function (res) {
      return res.text().then(function (txt) {
        var json = null;
        try { json = txt ? JSON.parse(txt) : null; } catch (e) { json = null; }
        return { ok: res.ok, status: res.status, json: json };
      });
    }).then(function (r) {
      if (timer) clearTimeout(timer);
      return r;
    }, function (err) {
      if (timer) clearTimeout(timer);
      var e = new Error(timedOut ? 'timeout' : 'network');
      e.cause = err;
      throw e;
    });
  }

  function apiMessage(result, fallback) {
    var j = result && result.json;
    if (j) {
      if (typeof j.message === 'string' && j.message.trim()) return j.message.trim();
      if (typeof j.error === 'string' && j.error.trim()) return j.error.trim();
      if (j.error && typeof j.error.message === 'string' && j.error.message.trim()) return j.error.message.trim();
    }
    return fallback;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* ---------- one landing page instance ---------- */

  function init(root) {
    if (!root || root.getAttribute('data-vc-ready') === 'true') return;
    root.setAttribute('data-vc-ready', 'true');

    var apiBase = String(root.getAttribute('data-api-base') || '').trim().replace(/\/+$/, '');
    var shopDomain = String(root.getAttribute('data-shop-domain') || '').trim();
    var useProxy = !apiBase || apiBase === 'proxy'; // blank or sentinel "proxy" -> go through the app proxy
    var AVAILABLE_SLOTS_PATH = useProxy ? '/apps/jewel/video-booking/available-slots' : '/api/v1/bookings/storefront/available-slots';
    var BOOKING_PATH = useProxy ? '/apps/jewel/video-booking' : '/api/v1/bookings/storefront';
    if (useProxy) apiBase = '';

    var form = root.querySelector('[data-vc-form]');
    if (!form) return;

    var nameEl = root.querySelector('#ttj-vc-name');
    var emailEl = root.querySelector('#ttj-vc-email');
    var phoneEl = root.querySelector('#ttj-vc-phone');
    var phoneCodeEl = root.querySelector('[data-vc-phone-code]');
    var dateEl = root.querySelector('#ttj-vc-date');

    var catBoxes = [].slice.call(root.querySelectorAll('[data-vc-cat]'));
    var otherBox = catBoxes.filter(function (b) { return b.value === 'other'; })[0] || null;
    var otherField = root.querySelector('[data-vc-other-field]');
    var otherInput = root.querySelector('[data-vc-other-input]');
    var otherCount = root.querySelector('[data-vc-other-count]');

    var slotsWrap = root.querySelector('[data-vc-slots-wrap]');
    var slotsMsg = root.querySelector('[data-vc-slots-msg]');
    var slotsRetry = root.querySelector('[data-vc-slots-retry]');
    var slotsEl = root.querySelector('[data-vc-slots]');

    var submitBtn = root.querySelector('[data-vc-submit]');
    var submitError = root.querySelector('[data-vc-submit-error]');
    var successEl = root.querySelector('[data-vc-success]');
    var successTitle = root.querySelector('[data-vc-success-title]');
    var successSummary = root.querySelector('[data-vc-success-summary]');

    var state = { time: '', slotsRequestId: 0, slotsDate: '', busy: false };

    function apiUrl(path) { return apiBase + path; }

    /* ---------- CTA: smooth scroll to the booking section ---------- */

    [].forEach.call(root.querySelectorAll('[data-vc-scroll]'), function (link) {
      link.addEventListener('click', function (e) {
        var target = document.getElementById(link.getAttribute('data-vc-scroll'));
        if (!target) return; // falls back to the normal #anchor jump
        e.preventDefault();
        target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        if (window.history && typeof window.history.replaceState === 'function') {
          window.history.replaceState(null, '', '#' + target.id);
        }
        window.setTimeout(function () {
          if (nameEl) nameEl.focus({ preventScroll: true });
        }, prefersReducedMotion() ? 0 : 600);
      });
    });

    /* ---------- errors ---------- */

    function fieldFor(key) {
      return { name: nameEl, email: emailEl, phone: phoneEl, date: dateEl }[key] || null;
    }

    function setError(key, message) {
      var err = root.querySelector('[data-vc-error="' + key + '"]');
      var field = fieldFor(key);
      if (!err) return;
      if (message) {
        setText(err, message);
        show(err, true);
        if (field) field.setAttribute('aria-invalid', 'true');
      } else {
        setText(err, '');
        show(err, false);
        if (field) field.removeAttribute('aria-invalid');
      }
    }

    function clearErrors(keys) {
      keys.forEach(function (k) { setError(k, ''); });
    }

    /* ---------- categories + "Specify other" ---------- */

    function selectedCategories() {
      return catBoxes.filter(function (b) { return b.checked; });
    }

    function updateOtherCount() {
      if (otherCount && otherInput) setText(otherCount, otherInput.value.length + ' / ' + OTHER_MAX);
    }

    // "Specify other": visible only while Other is selected (alone or with others); the value is cleared when it is
    // deselected so stale text can never be submitted.
    function syncOther() {
      if (!otherField || !otherInput || !otherBox) return;
      var on = otherBox.checked;
      show(otherField, on);
      if (!on) otherInput.value = '';
      updateOtherCount();
    }

    function otherText() {
      return otherBox && otherBox.checked && otherInput ? otherInput.value.trim() : '';
    }

    catBoxes.forEach(function (box) {
      box.addEventListener('change', function () {
        syncOther();
        if (box === otherBox && box.checked && otherInput) otherInput.focus();
      });
    });

    if (otherInput) otherInput.addEventListener('input', updateOtherCount);

    /* ---------- available slots (existing storefront API, no hardcoded times) ---------- */

    function setSlotsBusy(on) { slotsWrap.setAttribute('aria-busy', on ? 'true' : 'false'); }

    // Three-dot typing indicator (decorative; announced via aria-label on the wrapper).
    function appendDots(el) {
      for (var i = 0; i < 3; i++) {
        var d = document.createElement('span');
        d.className = 'ttj-vc-dots__dot';
        el.appendChild(d);
      }
    }

    function slotsLoadingDots() {
      slotsMsg.textContent = '';
      var dots = document.createElement('span');
      dots.className = 'ttj-vc-dots ttj-vc-dots--slots';
      dots.setAttribute('role', 'img');
      dots.setAttribute('aria-label', 'Loading available times');
      appendDots(dots);
      slotsMsg.appendChild(dots);
      slotsMsg.classList.remove('is-error');
      show(slotsMsg, true);
      show(slotsRetry, false);
      slotsEl.textContent = '';
      show(slotsEl, false);
    }

    function slotsMessage(text, opts) {
      opts = opts || {};
      setText(slotsMsg, text);
      slotsMsg.classList.toggle('is-error', !!opts.error);
      show(slotsMsg, true);
      show(slotsRetry, !!opts.retry);
      slotsEl.textContent = '';
      show(slotsEl, false);
    }

    function renderSlots(times, keep) {
      slotsEl.textContent = '';
      times.forEach(function (t, i) {
        var label = document.createElement('label');
        label.className = 'ttj-vc-slot';
        var input = document.createElement('input');
        input.type = 'radio';
        input.name = 'ttj-vc-slot';
        input.value = t;
        input.id = 'ttj-vc-slot-' + i;
        if (keep && keep === t) input.checked = true;
        var span = document.createElement('span');
        span.textContent = formatTime(t);
        label.appendChild(input);
        label.appendChild(span);
        slotsEl.appendChild(label);
      });
      show(slotsMsg, false);
      show(slotsRetry, false);
      show(slotsEl, true);
    }

    function resetSlots(message) {
      state.slotsRequestId++; // invalidate any in-flight request
      state.slotsDate = '';
      state.time = '';
      setSlotsBusy(false);
      slotsMessage(message || 'Choose a date to see available times.');
    }

    function loadSlots(date) {
      var keep = state.time;
      var reqId = ++state.slotsRequestId;
      state.slotsDate = date;
      state.time = '';
      setError('time', '');
      setSlotsBusy(true);
      slotsLoadingDots();

      var url = apiUrl(AVAILABLE_SLOTS_PATH) +
        '?date=' + encodeURIComponent(date) +
        (useProxy ? '' : '&shopDomain=' + encodeURIComponent(shopDomain));

      request(url, { method: 'GET', headers: { Accept: 'application/json' } }, SLOTS_TIMEOUT_MS)
        .then(function (res) {
          if (reqId !== state.slotsRequestId) return; // a newer date was chosen meanwhile
          setSlotsBusy(false);
          var times = res.json && res.json.data && res.json.data.times;
          if (!res.ok || !res.json || res.json.success !== true || !Array.isArray(times)) {
            diag('available-slots returned an unexpected response', { url: url, httpStatus: res.status, ok: res.ok, body: res.json });
            state.slotsDate = '';
            slotsMessage(apiMessage(res, 'We could not load available times right now. Please try again.'), { error: true, retry: true });
            return;
          }
          var clean = times.filter(isValidTimeValue);
          if (!clean.length) {
            slotsMessage('No available slots for this date. Please try another date.');
            return;
          }
          renderSlots(clean, keep);
          var chosen = slotsEl.querySelector('input:checked');
          state.time = chosen ? chosen.value : '';
        })
        .catch(function (err) {
          if (reqId !== state.slotsRequestId) return;
          setSlotsBusy(false);
          state.slotsDate = '';
          diag('available-slots request failed before a response was readable (network / CORS / DNS / host asleep)', { url: url, reason: err && err.message, cause: err && err.cause && String(err.cause) });
          slotsMessage(
            err && err.message === 'timeout'
              ? 'Loading times is taking too long. Please try again.'
              : 'We could not reach the booking service. Please check your connection and try again.',
            { error: true, retry: true }
          );
        });
    }

    function onDateChange() {
      var v = dateEl.value;
      setError('date', '');
      if (!v) { resetSlots(); return; }
      if (!isSelectableDate(v)) {
        resetSlots('Choose a valid upcoming date to see available times.');
        setError('date', 'Please choose a valid upcoming date.');
        return;
      }
      if (v === state.slotsDate) return; // "input" and "change" both fire on most browsers: one request only
      loadSlots(v);
    }

    dateEl.addEventListener('change', onDateChange);
    dateEl.addEventListener('input', function () {
      // Browsers that fire only "input" (or text fallbacks) still trigger the fetch once the value is a full date.
      if (dateEl.value !== state.slotsDate && isIsoDate(dateEl.value)) onDateChange();
    });

    slotsRetry.addEventListener('click', function () {
      if (isSelectableDate(dateEl.value)) loadSlots(dateEl.value);
    });

    slotsEl.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'ttj-vc-slot') {
        state.time = e.target.value;
        setError('time', '');
      }
    });

    /* ---------- phone: country-code selector + local number ---------- */

    var PHONE_MAX_DIGITS = 15; // E.164 maximum, dial code included

    function dialCode() {
      return phoneCodeEl ? String(phoneCodeEl.value || '').replace(/\D/g, '') : '';
    }

    function localPhone() {
      return String(phoneEl.value || '').replace(/\D/g, '');
    }

    // Digits only: dial code + local number, no "+" and no separators. One leading 0 (national trunk prefix)
    // is dropped from the local number: "91" + "09876543210" -> "919876543210"; "9876543210" is unchanged.
    function fullPhone() {
      return dialCode() + localPhone().replace(/^0/, '');
    }

    function syncPhoneInput() {
      var clean = localPhone();
      var max = Math.max(4, PHONE_MAX_DIGITS - dialCode().length);
      phoneEl.setAttribute('maxlength', String(max));
      if (phoneEl.value !== clean.slice(0, max)) phoneEl.value = clean.slice(0, max);
    }

    phoneEl.addEventListener('input', function () {
      syncPhoneInput();
      setError('phone', '');
    });
    if (phoneCodeEl) {
      phoneCodeEl.addEventListener('change', function () {
        syncPhoneInput();
        setError('phone', '');
      });
    }
    syncPhoneInput();

    /* ---------- validation ---------- */

    function validateAll() {
      var firstBad = null;
      clearErrors(['name', 'email', 'phone', 'date', 'time']);

      var name = nameEl.value.trim();
      var email = emailEl.value.trim();

      if (!name) {
        setError('name', 'Please enter your name.');
        firstBad = firstBad || nameEl;
      } else if (name.length > NAME_MAX) {
        setError('name', 'Please use ' + NAME_MAX + ' characters or fewer.');
        firstBad = firstBad || nameEl;
      }
      if (!email) {
        setError('email', 'Please enter your email address.');
        firstBad = firstBad || emailEl;
      } else if (email.length > EMAIL_MAX || /\.\./.test(email) || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        setError('email', 'Please enter a valid email address.');
        firstBad = firstBad || emailEl;
      }
      var local = localPhone();
      var combined = fullPhone();
      if (!local) {
        setError('phone', 'Please enter your phone number.');
        firstBad = firstBad || phoneEl;
      } else if (local.replace(/^0/, '').length < 4 || combined.length < 7 || combined.length > PHONE_MAX_DIGITS) {
        setError('phone', 'Please enter a valid phone number.');
        firstBad = firstBad || phoneEl;
      }

      var date = dateEl.value;
      if (!date) {
        setError('date', 'Please choose a date.');
        firstBad = firstBad || dateEl;
      } else if (!isSelectableDate(date)) {
        setError('date', 'Please choose a valid upcoming date.');
        firstBad = firstBad || dateEl;
      } else if (!state.time) {
        setError('time', 'Please choose an available time.');
        firstBad = firstBad || slotsEl.querySelector('input[type="radio"]');
      }

      if (firstBad) firstBad.focus();
      return !firstBad;
    }

    /* ---------- submit (existing storefront booking endpoint + payload) ---------- */

    function addRow(dl, term, value) {
      var row = document.createElement('div');
      row.className = 'ttj-vc-summary__row';
      var dt = document.createElement('dt');
      dt.textContent = term;
      var dd = document.createElement('dd');
      dd.textContent = value;
      row.appendChild(dt);
      row.appendChild(dd);
      dl.appendChild(row);
    }

    function setBusy(on) {
      state.busy = on;
      submitBtn.disabled = on;
      submitBtn.classList.toggle('is-processing', on);
      if (on) {
        submitBtn.textContent = '';
        var lbl = document.createElement('span');
        lbl.textContent = 'Processing';
        var dots = document.createElement('span');
        dots.className = 'ttj-vc-dots ttj-vc-dots--btn';
        dots.setAttribute('aria-hidden', 'true');
        appendDots(dots);
        submitBtn.appendChild(lbl);
        submitBtn.appendChild(dots);
      } else {
        submitBtn.textContent = submitBtn.getAttribute('data-label') || 'Confirm Consultation';
      }
      form.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    function buildPayload() {
      var payload = {
        customerName: nameEl.value.trim(),
        customerEmail: emailEl.value.trim(),
        customerPhone: fullPhone(),
        date: dateEl.value,
        time: state.time
      };
      var cats = selectedCategories().map(function (b) { return b.value; });
      if (cats.length) payload.categories = cats;
      // The API has no field for this, so the "Specify other" text travels in the existing optional notes field.
      if (otherText()) payload.notes = 'Other: ' + otherText();
      if (!useProxy) payload.shopDomain = shopDomain;
      return payload;
    }

    function showSubmitError(message) {
      setText(submitError, message);
      show(submitError, true);
    }

    function refreshSlotsAfterFailure() {
      // The chosen slot may have been taken meanwhile: reload the times for the same date.
      if (isSelectableDate(dateEl.value)) loadSlots(dateEl.value);
    }

    function submitBooking() {
      if (state.busy) return; // no duplicate submissions
      setBusy(true);
      show(submitError, false);
      var payload = buildPayload();

      request(apiUrl(BOOKING_PATH), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }, BOOK_TIMEOUT_MS)
        .then(function (res) {
          setBusy(false);
          if (!res.ok || !res.json || res.json.success !== true) {
            showSubmitError(apiMessage(res, 'We could not complete your booking. Please choose another time, or try again in a moment.'));
            refreshSlotsAfterFailure();
            return;
          }
          var d = res.json.data || {};
          successSummary.textContent = '';
          addRow(successSummary, 'Date', formatDate(d.bookingDate || payload.date));
          addRow(successSummary, 'Time', formatTime(d.bookingTime || payload.time));
          show(form, false);
          show(successEl, true);
          if (successEl.scrollIntoView) successEl.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
          if (successTitle) successTitle.focus({ preventScroll: true });
        })
        .catch(function (err) {
          setBusy(false);
          showSubmitError(
            err && err.message === 'timeout'
              ? 'The booking service took too long to respond. Your booking may not have been saved, so please try again.'
              : 'We could not reach the booking service. Please check your connection and try again.'
          );
        });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (state.busy) return;
      if (validateAll()) submitBooking();
    });

    /* ---------- init ---------- */

    dateEl.min = todayIso();
    dateEl.max = maxIso();
    syncOther();
    resetSlots();
  }

  function initAll() {
    [].forEach.call(document.querySelectorAll('[data-ttj-vc]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-initialise when the section is re-rendered.
  document.addEventListener('shopify:section:load', initAll);
})();
